import { ArrowRight, Play, CheckCircle2, Zap, Users, Clock } from 'lucide-react';
import { motion } from 'framer-motion';

export default function Hero() {
  const stats = [
    { icon: Zap, label: 'Faster Response', value: '10x' },
    { icon: Users, label: 'Applicants Hired', value: '500+' },
    { icon: Clock, label: 'Time Saved', value: '20hrs/wk' },
  ];

  return (
    <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 gradient-bg -z-10" />
      
      {/* Grid pattern */}
      <div 
        className="absolute inset-0 -z-10 opacity-[0.015]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      <div className="container-custom">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-8">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-500"></span>
              </span>
              Now with AI-powered responses
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight text-gray-900 mb-6"
          >
            Hire faster with{' '}
            <span className="gradient-text">smart automation</span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-lg sm:text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed"
          >
            Automatically email applicants when they apply, let them schedule interviews
            via Calendly, and manage everything from one dashboard. No more phone tag.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16"
          >
            <a
              href="/app"
              className="btn-primary text-base px-8 py-4 w-full sm:w-auto group"
            >
              Start Hiring Free
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#how-it-works"
              className="btn-secondary text-base px-8 py-4 w-full sm:w-auto"
            >
              <Play className="mr-2 w-4 h-4" />
              See How It Works
            </a>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="grid grid-cols-3 gap-8 max-w-lg mx-auto"
          >
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <stat.icon className="w-5 h-5 text-primary-600" />
                </div>
                <div className="text-2xl sm:text-3xl font-bold text-gray-900">{stat.value}</div>
                <div className="text-xs sm:text-sm text-gray-500">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Dashboard Preview */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="mt-16 lg:mt-24"
        >
          <div className="relative max-w-5xl mx-auto">
            {/* Browser chrome */}
            <div className="bg-gray-900 rounded-t-xl p-3 flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <div className="w-3 h-3 rounded-full bg-green-500" />
              </div>
              <div className="flex-1 text-center">
                <div className="inline-flex items-center gap-2 px-4 py-1 bg-gray-800 rounded-md text-xs text-gray-400">
                  app.ihirefast.com
                </div>
              </div>
            </div>
            
            {/* Screenshot placeholder */}
            <div className="bg-white rounded-b-xl shadow-2xl border border-gray-200 overflow-hidden">
              <div className="aspect-[16/9] bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
                <div className="text-center p-8">
                  <div className="grid grid-cols-3 gap-4 max-w-3xl mx-auto">
                    {/* Sidebar mock */}
                    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 h-64">
                      <div className="w-8 h-8 bg-primary-100 rounded-lg mb-4" />
                      <div className="space-y-2">
                        <div className="h-2 bg-gray-200 rounded w-3/4" />
                        <div className="h-2 bg-gray-200 rounded w-1/2" />
                        <div className="h-2 bg-gray-200 rounded w-2/3" />
                      </div>
                    </div>
                    {/* Chat mock */}
                    <div className="col-span-2 bg-white rounded-lg p-4 shadow-sm border border-gray-100 h-64">
                      <div className="flex gap-3 mb-4">
                        <div className="w-10 h-10 bg-primary-100 rounded-full" />
                        <div className="flex-1">
                          <div className="h-3 bg-gray-800 rounded w-1/3 mb-2" />
                          <div className="h-2 bg-gray-200 rounded w-1/4" />
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="flex justify-end">
                          <div className="bg-primary-600 text-white text-xs px-3 py-2 rounded-lg max-w-[80%]">
                            Hi! Thanks for applying. When can you interview?
                          </div>
                        </div>
                        <div className="flex">
                          <div className="bg-gray-100 text-gray-800 text-xs px-3 py-2 rounded-lg max-w-[80%]">
                            I&apos;m free tomorrow at 2pm!
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating elements */}
            <div className="absolute -top-4 -right-4 bg-white rounded-lg shadow-lg p-3 border border-gray-100 animate-pulse-slow hidden lg:block">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                </div>
                <div className="text-sm">
                  <div className="font-medium text-gray-900">Message Sent</div>
                  <div className="text-gray-500 text-xs">Just now</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
